import React, { useCallback, useEffect, useRef, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import InfiniteScroll from 'external/infiniteScroll/app'
import isEmpty from 'lodash/isEmpty'
import { FlagIcon } from 'images'
import { ReleasesService } from 'services'
import { thunks as DownloadsThunks } from 'reducers/Downloads'
import FlaggedActions, { thunks as FlaggedThunks } from 'reducers/Flagged'
import { CollectionContent, DialogModal, ReleasesTable } from 'components'
import FlaggedReleaseModal from 'components/Modals/FlaggedReleaseModal'
import { RESPONSIVE_BREAKPOINT_PAGES } from 'constants/responsiveBreakpoints'
import { PLAY_SOURCES } from 'constants/tracking'
import { useResponsiveBreakpointsVersion } from 'contexts/ResponsiveBreakpointsContext'
import flaggedStyles from './Flagged.module.scss'

export const FlaggedContent = () => {
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const responsiveBreakpointsVersion = useResponsiveBreakpointsVersion()
  const didLoadRef = useRef(false)
  const [showClearListModal, setShowClearListModal] = useState(false)
  const { releases, page, totalItemsCount, loading } = useSelector(
    ({ flagged }) => ({
      releases: flagged.releases,
      page: flagged.page,
      totalItemsCount: flagged.totalItemsCount,
      loading: flagged.loading
    }),
    shallowEqual
  )

  const onLoadMore = useCallback(() => {
    if (!loading) {
      dispatch(FlaggedThunks.search(page + 1))
    }
  }, [dispatch, loading, page])

  useEffect(() => {
    if (didLoadRef.current) {
      return
    }

    didLoadRef.current = true

    if (page === 0) {
      dispatch(FlaggedThunks.search(1))
    } else {
      dispatch(FlaggedThunks.refreshList())
    }
  }, [dispatch, page])

  const handleDownloadRelease = useCallback(
    async (release, format) => {
      const releaseDetail = await ReleasesService.getDetail(release.releaseid)

      if (!isEmpty(releaseDetail.data)) {
        dispatch(
          DownloadsThunks.addReleaseToDownloadQueue(
            release,
            format,
            'flagged'
          )
        )
      }
    },
    [dispatch]
  )

  const handleRemoveRelease = useCallback(
    (release) => {
      dispatch(
        FlaggedThunks.flag(
          release.releaseid,
          false,
          () => {
            dispatch(FlaggedActions.removeFlaggedRelease(release))
          },
          () => {}
        )
      )
    },
    [dispatch]
  )

  const handleOpenClearListModal = useCallback(() => {
    setShowClearListModal(true)
  }, [])

  const handleCloseClearListModal = useCallback(() => {
    setShowClearListModal(false)
  }, [])

  const handleClearAll = useCallback(() => {
    dispatch(FlaggedThunks.clearAll())
    setShowClearListModal(false)
  }, [dispatch])

  const hasMorePage =
    totalItemsCount === null || totalItemsCount > releases.length

  const modalProps = {
    handleRemoveClick: handleRemoveRelease,
    handleDownloadClick: handleDownloadRelease
  }

  const renderClearListModal = () => {
    return (
      <DialogModal
        title={t('collections.clear-flagged')}
        onClickYes={handleClearAll}
        onClickCancel={handleCloseClearListModal}
      >
        <div className="w-full flex flex-col items-center my-10 text-sm font-header">
          {t('collections.dialog-body')}
        </div>
      </DialogModal>
    )
  }

  const renderTable = () => {
    return (
      <div id="flaggedScrollableDiv" className={flaggedStyles.scrollableDiv}>
        <InfiniteScroll
          dataLength={releases.length}
          hasMore={hasMorePage}
          loader={<div aria-hidden="true" />}
          next={onLoadMore}
          scrollableTarget="flaggedScrollableDiv"
        >
          <ReleasesTable
            flagged
            downloadSource="flagged_releases_table"
            playSource={PLAY_SOURCES.FLAGGED}
            items={releases}
            modalComponent={FlaggedReleaseModal}
            modalProps={modalProps}
          />
        </InfiniteScroll>
      </div>
    )
  }

  return (
    <div
      className="flex h-full flex-col"
      data-responsive-breakpoints={responsiveBreakpointsVersion}
      data-responsive-breakpoints-page={RESPONSIVE_BREAKPOINT_PAGES.FLAGGED}
    >
      <CollectionContent
        disableContentScroll
        actionTitle={t('collections.clear-list')}
        icon={<FlagIcon />}
        table={renderTable()}
        title={t('collections.flagged')}
        actionCallback={handleOpenClearListModal}
        actionDisabled={releases.length === 0}
      />
      {showClearListModal && renderClearListModal()}
    </div>
  )
}

export default FlaggedContent
